import { Chart, type ChartOptions, registerables } from 'chart.js';
import { useMemo } from 'react';

import { useIntl } from '~/hooks/useIntl';
import { fontFamily, neutralPalette } from '~/styles/constants.css';

Chart.register(...registerables);

export const useStackedBarChartOptions = (hasData: boolean): ChartOptions<'bar'> => {
  const { formatCurrency } = useIntl();

  return useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: { top: 4, right: 8, bottom: 4, left: 4 },
      },
      scales: {
        y: {
          stacked: true,
          beginAtZero: true,
          ...(!hasData && { suggestedMax: 5000 }),
          offset: false,
          position: 'right',
          afterFit(scale) {
            scale.width += 16;
          },
          ticks: {
            callback: (value) => formatCurrency(Number(value)),
            color: neutralPalette.gray.$800,
            padding: 8,
            font: { size: 14, family: fontFamily.secondary },
          },
        },
        x: {
          stacked: true,
          grid: { display: false },
          afterFit(scale) {
            scale.height += 8;
          },
          ticks: {
            color: neutralPalette.gray.$800,
            padding: 4,
            font: { size: 14, family: fontFamily.secondary },
          },
        },
      },
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            color: neutralPalette.gray.$800,
            padding: 24,
            boxWidth: 24,
            boxHeight: 24,
            useBorderRadius: true,
            borderRadius: 4,
            font: { size: 14, family: fontFamily.secondary },
          },
        },
        tooltip: {
          backgroundColor: neutralPalette.gray.$800,
          padding: 12,
          cornerRadius: 4,
          titleFont: () => ({ size: 16, family: fontFamily.secondary, weight: 500 }),
          bodyFont: () => ({ size: 14, family: fontFamily.secondary, weight: 400 }),
          callbacks: {
            label: (context) => {
              let label = context.dataset.label || '';

              if (label) {
                label += ': ';
              }

              if (context.parsed.y !== null) {
                label += formatCurrency(context.parsed.y);
              }

              return label;
            },
          },
        },
      },
    }),
    [formatCurrency, hasData],
  );
};
